import type { MutableRefObject } from 'react';

import { consoleLogDev } from 'common/helpers/consoleLogDev';
import { sendAnalyticsWarning } from 'features/analytics/helpers';
import { shareTextAllCleared } from 'features/share-text/store';
import { VrsChatEventBus } from 'features/vrs-chat/services';
import { resetVrsChatState } from 'features/vrs-chat/store';
import {
  DATA_CHANNEL_LOG_GROUP,
  logDataChannelCleanup,
} from 'features/sip/helpers';
import type { DataChannelMetrics } from 'features/sip/helpers';
import type { AppDispatch } from 'features/app/store/store';

export interface CloseAndCleanupParams {
  dispatch: AppDispatch;
  channelRef: MutableRefObject<RTCDataChannel | null>;
  cleanupInProgressRef: MutableRefObject<boolean>;
  creatingChannelRef?: MutableRefObject<boolean>;
  trackers: {
    received: { flush: () => void; cancel: () => void };
    sent: { flush: () => void; cancel: () => void };
  };
  metricsRef: MutableRefObject<DataChannelMetrics>;
  reason: string;
}

export const closeAndCleanupChannel = ({
  dispatch,
  channelRef,
  cleanupInProgressRef,
  creatingChannelRef,
  trackers,
  metricsRef,
  reason,
}: CloseAndCleanupParams) => {
  if (creatingChannelRef) {
    try {
      creatingChannelRef.current = false;
    } catch (e) {
      consoleLogDev('DEBUG: clearing creatingChannelRef exception -', e);
    }
  }

  if (cleanupInProgressRef.current) {
    return;
  }

  cleanupInProgressRef.current = true;

  // Flush analytics to capture any pending counts before cleanup
  trackers.received.flush();
  trackers.sent.flush();

  const channel = channelRef.current;
  channelRef.current = null;

  if (channel) {
    try {
      channel.onopen = null;
      channel.onmessage = null;
      channel.onclosing = null;
      channel.onclose = null;
      channel.onerror = null;
    } catch (e) {
      consoleLogDev('DEBUG: detachHandlers exception -', e);
    }

    if (channel.readyState === 'closed') {
      consoleLogDev('DEBUG: DataChannel already closed', {
        reason,
        readyState: channel.readyState,
      });
    } else {
      try {
        channel.close();
      } catch (e) {
        consoleLogDev('DEBUG: DataChannel close exception -', e);
        dispatch(
          sendAnalyticsWarning({
            Method: 'closeAndCleanupChannel',
            LogGroup: DATA_CHANNEL_LOG_GROUP,
            Message: {
              event: 'data_channel_close_exception',
              reason,
            },
          })
        );
      }
    }
  }

  if (reason !== 'onclose') {
    logDataChannelCleanup(dispatch, reason, metricsRef.current);
  }

  // Reset metrics
  metricsRef.current = {
    messagesReceived: 0,
    messagesSent: 0,
  };

  VrsChatEventBus.$clearMessage.next();
  dispatch(shareTextAllCleared());
  dispatch(resetVrsChatState());

  // Allow future cleanup after this one completes
  cleanupInProgressRef.current = false;
};
